import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import {
  Button,
  Card,
  CardContent,
  Link,
  TextField,
} from '@material-ui/core';

import * as events from '../../../events';
import * as actions from '../../../state';
import styles from './styles.module.css';
import AppleSso from './AppleSso';
import GoogleSso from './GoogleSso';
import OfficeSso from './OfficeSso';
import LoginMessageSnackbar from './LoginMessageSnackbar';

const messages = defineMessages({
  ANALYTICS: {
    id: 'ANALYTICS',
    defaultMessage: 'Analytics',
  },
  LOGO: {
    id: 'LOGO',
    defaultMessage: 'Logo',
  },
  LOG_IN: {
    id: 'LOG_IN',
    defaultMessage: 'Log In',
  },
  USERNAME_REQUIRED: {
    id: 'USERNAME_REQUIRED',
    defaultMessage: 'Username required',
  },
  PASSWORD_REQUIRED: {
    id: 'PASSWORD_REQUIRED',
    defaultMessage: 'Password required',
  },
  LOGIN_NAME: {
    id: 'LOGIN_NAME',
    defaultMessage: 'Login Name',
  },
  PASSWORD: {
    id: 'PASSWORD',
    defaultMessage: 'Password',
  },
  FORGOT_LOGIN_NAME: {
    id: 'FORGOT_LOGIN_NAME',
    defaultMessage: 'Forgot login name',
  },
  FORGOT_PASSWORD: {
    id: 'FORGOT_PASSWORD',
    defaultMessage: 'Forgot password',
  },
});

function LoginForm({
  intl,
}) {
  const dispatch = useDispatch();

  const configs = useSelector((state) => state.configs);
  const loginImage = useSelector(actions.selectLoginImage);

  // eslint-disable-next-line max-len
  const appleSsoEnabled = useSelector((state) => actions.selectConfig(state, `PORTAL_LOGIN_APPLE_SSO_ENABLED_${window.location.hostname}`)
    || actions.selectConfig(state, 'PORTAL_LOGIN_APPLE_SSO_ENABLED'));
  // eslint-disable-next-line max-len
  const appleSsoClientId = useSelector((state) => actions.selectConfig(state, `PORTAL_LOGIN_APPLE_SSO_CLIENT_ID_${window.location.hostname}`)
    || actions.selectConfig(state, 'PORTAL_LOGIN_APPLE_SSO_CLIENT_ID'));

  // eslint-disable-next-line max-len
  const googleSsoEnabled = useSelector((state) => actions.selectConfig(state, `PORTAL_LOGIN_GOOGLE_SSO_ENABLED_${window.location.hostname}`)
    || actions.selectConfig(state, 'PORTAL_LOGIN_GOOGLE_SSO_ENABLED'));
  // eslint-disable-next-line max-len
  const googleSsoClientId = useSelector((state) => actions.selectConfig(state, `PORTAL_LOGIN_GOOGLE_SSO_CLIENT_ID_${window.location.hostname}`)
    || actions.selectConfig(state, 'PORTAL_LOGIN_GOOGLE_SSO_CLIENT_ID'));

  // eslint-disable-next-line max-len
  const officeSsoEnabled = useSelector((state) => actions.selectConfig(state, `PORTAL_LOGIN_OFFICE_SSO_ENABLED_${window.location.hostname}`)
    || actions.selectConfig(state, 'PORTAL_LOGIN_OFFICE_SSO_ENABLED'));
  // eslint-disable-next-line max-len
  const officeSsoClientId = useSelector((state) => actions.selectConfig(state, `PORTAL_LOGIN_OFFICE_SSO_CLIENT_ID_${window.location.hostname}`)
    || actions.selectConfig(state, 'PORTAL_LOGIN_OFFICE_SSO_CLIENT_ID'));

  const appleSsoEnabledAndConfigured = appleSsoEnabled && appleSsoClientId;
  const googleSsoEnabledAndConfigured = googleSsoEnabled && googleSsoClientId;
  const officeSsoEnabledAndConfigured = officeSsoEnabled && officeSsoClientId;

  const [username, setUsername] = useState('');
  const [usernameErr, setUsernameErr] = useState('');
  const [password, setPassword] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [authAttempted, setAuthAttempted] = useState(false);

  useEffect(() => {
    document.title = configs?.PORTAL_ANALYTICS_FEATURE_NAME || intl.formatMessage({ ...messages.ANALYTICS });
  }, []);

  const onPasswordChange = (e) => {
    setPassword(e.target.value);

    if (!e.target.value) {
      setPasswordErr(intl.formatMessage({ ...messages.PASSWORD_REQUIRED }));
    } else {
      setPasswordErr('');
    }
  };

  const onUsernameChange = (e) => {
    setUsername(e.target.value || '');

    if (!e.target.value) {
      setUsernameErr(intl.formatMessage({ ...messages.USERNAME_REQUIRED }));
    } else {
      setUsernameErr('');
    }
  };

  const userLogin = (e) => {
    e.preventDefault();

    if (!username) {
      setUsernameErr(intl.formatMessage({ ...messages.USERNAME_REQUIRED }));
      setPasswordErr('');
      setAuthAttempted(true);
    } else if (!password) {
      setUsernameErr('');
      setPasswordErr(intl.formatMessage({ ...messages.PASSWORD_REQUIRED }));
      setAuthAttempted(true);
    } else {
      setUsernameErr('');
      setPasswordErr('');
      dispatch(events.authenticateUser(username, password));
    }
  };

  const resetForm = () => {
    setAuthAttempted(false);
  };

  const routeToForgotLogin = () => {
    dispatch(actions.showForgotLoginPage(true));
  };

  const routeToForgotPassword = () => {
    dispatch(actions.showForgotPasswordPage(true));
  };

  const ssoEnabled = appleSsoEnabledAndConfigured || googleSsoEnabledAndConfigured || officeSsoEnabledAndConfigured;

  return (
    <div className={`${styles.loginCard} animate__animated animate__fadeIn`}>
      <Card className={styles.card}>
        <div className={styles.media}>
          <img
            src={loginImage}
            title={intl.formatMessage({ ...messages.LOGO })}
            alt={intl.formatMessage({ ...messages.LOGO })}
          />
        </div>

        <CardContent className={styles.content} classes={{ root: styles.loginFormCard }}>
          <form onSubmit={userLogin} noValidate>
            <TextField
              required
              id="name"
              onFocus={resetForm}
              className={styles.textField}
              onBlur={onUsernameChange}
              onChange={onUsernameChange}
              style={{ marginBottom: 12 }}
              label={intl.formatMessage({ ...messages.LOGIN_NAME })}
              error={authAttempted && Boolean(usernameErr)}
            />
            <TextField
              required
              id="password"
              type="password"
              onFocus={resetForm}
              className={styles.textField}
              onBlur={onPasswordChange}
              onChange={onPasswordChange}
              label={intl.formatMessage({ ...messages.PASSWORD })}
              error={authAttempted && Boolean(passwordErr)}
            />
            <Button
              fullWidth
              size="large"
              type="submit"
              color="primary"
              variant="contained"
              className={styles.loginBtn}
            >
              <FormattedMessage {...messages.LOG_IN} />
            </Button>

            <div className={styles.loginLinks}>
              <Link to="/" onClick={routeToForgotLogin}>
                <FormattedMessage {...messages.FORGOT_LOGIN_NAME} />
              </Link>
              <span> | </span>
              <Link to="/" onClick={routeToForgotPassword}>
                <FormattedMessage {...messages.FORGOT_PASSWORD} />
              </Link>
            </div>

            {(ssoEnabled
                && (
                  <div className={styles.separatorContainer}>
                    <span className={styles.separator}>
                      <span className={styles.separatorContent}>OR</span>
                    </span>
                  </div>
                ))
              || ''}

            {(googleSsoEnabledAndConfigured && <GoogleSso />) || ''}
            {(appleSsoEnabledAndConfigured && <AppleSso />) || ''}
            {(officeSsoEnabledAndConfigured && <OfficeSso />) || ''}
          </form>
        </CardContent>
      </Card>

      <LoginMessageSnackbar />

    </div>
  );
}

export default injectIntl(LoginForm);
